import {
  ICreateTagCommand,
  PaginatedListOfTagDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { COACH_CLIENT_TAGS_LIST_QUERY_KEY } from "../CoachClientsTagsListScreen";
import TagsService from "../../../services/TagsService";
import { useCurrentUserId } from "../../../hooks/useCurrentUser";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import { COACH_CLIENTS_LIST_QUERY_KEY } from "../../coach-clients/CoachClientsListScreen";
import { TAGS_QUERY_KEY } from "../../../wrappers/router/coach/CoachClientsWrapper";

export const ALL_CLIENTS_TAGS_QUERY_KEY = "all-clients-tags";

export interface IQueryToInvalidate {
  queryKey: [string];
  refetchType?: "none" | "all" | "active" | "inactive";
}

export const INVALIDATE_KEYS_ON_CLIENT_TAGS_LIST_UPDATE: IQueryToInvalidate[] =
  [
    { queryKey: [COACH_CLIENTS_LIST_QUERY_KEY], refetchType: "none" },
    { queryKey: [ALL_CLIENTS_TAGS_QUERY_KEY] },
    {
      queryKey: [TAGS_QUERY_KEY],
    },
  ];

export const tagOnSuccessHandler = (
  queryClient: QueryClient,
  queries: IQueryToInvalidate[],
): void => {
  queries.forEach(({ queryKey, refetchType }) => {
    queryClient.invalidateQueries({
      queryKey,
      refetchType,
    });
  });
};

export function useCreateClientTag() {
  const coachId = useCurrentUserId();
  const queryClient = useQueryClient();

  return useOptimisticUpdateMutation({
    queryKey: [COACH_CLIENT_TAGS_LIST_QUERY_KEY],
    mutationFn: TagsService.createTag,
    successToastMessage: "Client tag created.",
    optimisticUpdater: {
      updateFn: (
        oldData: PaginatedListOfTagDto,
        variables: ICreateTagCommand,
        tempId: number,
      ) => {
        const newData = PaginatedListOfTagDto.fromJS(oldData);
        newData.totalCount = newData.items.unshift(
          TagDto.fromJS({
            ...variables,
            id: tempId,
            coachId,
          }),
        );
        return newData;
      },
    },
    successUpdater: {
      updateFn: (oldData, variables, tempId) => {
        oldData.items = oldData.items?.map((tag) => {
          if (tag.id === tempId) {
            tag.id = variables;
          }
          return tag;
        });
        return oldData;
      },
    },
    options: {
      onSuccess: () => {
        tagOnSuccessHandler(
          queryClient,
          INVALIDATE_KEYS_ON_CLIENT_TAGS_LIST_UPDATE,
        );
      },
    },
  });
}
